import { useState } from 'react';
import { Plus, LayoutTemplate, Users, Pencil, Trash2, MoreVertical, Zap, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';
import { useAudiences, useDeleteAudience } from '@/hooks/useNewsletter';
import type { Audience } from '@/types/newsletter';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { AudienceFormModal } from '@/components/newsletter/AudienceFormModal';

export function AudiencesPage() {
  const { user } = useAuth();
  const canWrite = user ? can(user.role, 'newsletter', 'write') : false;

  const { data, isLoading, error } = useAudiences();
  const del = useDeleteAudience();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Audience | null>(null);
  const [deleting, setDeleting] = useState<Audience | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  if (isLoading) return <LoaderScreen />;
  if (error) {
    return (
      <Card>
        <EmptyState icon={AlertCircle} title="Could not load audiences"
          description={(error as { message?: string })?.message ?? 'An unexpected error occurred.'} />
      </Card>
    );
  }

  async function confirmDelete() {
    if (!deleting) return;
    try {
      await del.mutateAsync(deleting.id);
      toast.success(`Deleted ${deleting.name}`);
      setDeleting(null);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Delete failed.');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="kicker mb-1">Audiences</div>
          <p className="text-sm text-text-muted">
            Segment your subscribers into lists or dynamic groups to target campaigns precisely.
          </p>
        </div>
        {canWrite && (
          <Button leftIcon={<Plus size={13} />} onClick={() => setCreateOpen(true)}>
            New Audience
          </Button>
        )}
      </div>

      {!data || data.length === 0 ? (
        <Card>
          <EmptyState icon={LayoutTemplate}
            title="No audiences yet"
            description="Create audiences to segment your 700+ subscribers into targeted groups — by language, client type, engagement, or any custom criteria."
            action={canWrite && (
              <Button leftIcon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
                Create First Audience
              </Button>
            )}
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.filter((a) => !a.archived).map((a) => (
            <Card key={a.id} className="relative hover:border-brass-400/60 transition-colors">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 rounded-sm border border-border" style={{ background: a.color ?? '#85572e' }} />
                  <Badge tone={a.kind === 'dynamic' ? 'brass' : 'neutral'}>
                    {a.kind === 'dynamic' ? <><Zap size={9} /> Dynamic</> : 'Static'}
                  </Badge>
                </div>
                {canWrite && (
                  <div className="relative">
                    <button onClick={() => setOpenMenu(openMenu === a.id ? null : a.id)}
                      className="p-1 text-text-dim hover:text-text hover:bg-bg-hover rounded-sm">
                      <MoreVertical size={14} />
                    </button>
                    {openMenu === a.id && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setOpenMenu(null)} />
                        <div className="absolute right-0 top-full mt-1 z-20 w-36 bg-bg-raised border border-border-strong rounded-sm shadow-raised py-1">
                          <button onClick={() => { setEditing(a); setOpenMenu(null); }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text hover:bg-bg-hover text-left">
                            <Pencil size={12} /> Edit
                          </button>
                          <button onClick={() => { setDeleting(a); setOpenMenu(null); }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-danger hover:bg-danger/10 text-left">
                            <Trash2 size={12} /> Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <h3 className="font-display text-lg text-text truncate">{a.name}</h3>
              {a.description && (
                <p className="text-xs text-text-muted mt-1 leading-relaxed line-clamp-2">{a.description}</p>
              )}

              <div className="mt-4 pt-4 border-t border-border flex items-center gap-2 text-xs text-text-muted">
                <Users size={11} />
                <span className="tabular">
                  {(a.subscriberCount ?? 0).toLocaleString()} subscriber{a.subscriberCount === 1 ? '' : 's'}
                </span>
              </div>

              {a.kind === 'dynamic' && a.rules && a.rules.length > 0 && (
                <div className="mt-2 text-2xs text-text-dim">
                  {a.rules.length} rule{a.rules.length === 1 ? '' : 's'}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <AudienceFormModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <AudienceFormModal open={!!editing} onClose={() => setEditing(null)} audience={editing} />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        loading={del.isPending}
        destructive
        title={`Delete ${deleting?.name}?`}
        confirmLabel="Delete audience"
        description="Subscribers remain — only the audience/segment is removed. Campaigns that referenced this audience keep their send history."
      />
    </div>
  );
}
